import { packageArtworkForOrder } from '@live-preview/artwork-order-package';
import { clearPerspectiveSurface } from '@patch/perspective-surface';
import { resolveSizing, adjustedAlignment } from '@patch/placement';
// assets/patch-preview.js
/**
 * Theme glue for the patch configurator.
 *
 * This is the only file in the patch system that knows about Vero, the product
 * form or the media gallery. Everything it orchestrates lives in the framework
 * free `@patch/*` modules, so moving the configurator into a theme app
 * extension means re-pointing the import map and re-rendering the markup, not
 * rewriting the engine.
 *
 * Responsibilities:
 *
 * - read merchant configuration out of the block
 * - own the single customisation state object
 * - drive the renderer that draws the patch over the product photo
 * - accept artwork from an upload or from the existing design picker
 * - keep line item properties and generated snapshot files in sync
 *
 * There is deliberately no second preview beside the controls. The product
 * photo is the preview, so the customer sees the patch in context and drags it
 * where it will actually sit.
 */

import { PLACEMENT_IDS, PLACEMENT_ICONS, PLACEMENT_ICON_VIEWBOX, constrainAdjustment, fitsBounds } from '@patch/placement';
import { bindPatchCondition } from './patch-conditional-display.js';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import {
  buildInitialState,
  getAlignment,
  getEngravingColor,
  getEngravingOptions,
  getMaterial,
  getMaterialGroup,
  getMaterialOption,
  getMaterialSetId,
  getPatchMaterials,
  mergePatchConfigSources,
  normalizeConfig,
  parseConfigJson,
} from '@patch/config';
import { PatchState, changed, createDefaultState } from '@patch/state';
import { CONTOUR_SHAPE_ID, PRESET_SHAPE_IDS, getShape, getShapes } from '@patch/shapes';
import {
  DEFAULT_ARTWORK_POSITION,
  buildShapeDisplayArtwork,
  clampPosition,
  loadArtworkFromFile,
  loadArtworkSource,
  releaseArtworkSource,
} from '@patch/artwork';
import {
  applyAlignment,
  describeMedia,
  listMedia,
  movePreviewAdjustment,
  resizePreviewAdjustment,
} from '@patch/alignment';
import { generateDieCutShape } from '@patch/diecut';
import { attachFileToInput, forgetDataUrl, exportProductPlacementPng } from '@patch/export';
import { createRemotePreviewRenderer } from '@patch/preview-remote';
import { collectTextureUrls, textureCache } from '@patch/materials';
import { createCleanedPreview } from '@patch/background-cleanup';
import { readCleanupControls, setupCleanupControls } from '@patch/cleanup-controls';

/** How long to wait after the last change before regenerating snapshot files. */
const SNAPSHOT_DEBOUNCE = 700;

/** Query parameters that switch on the merchant alignment editor. */
const ALIGNMENT_EDITOR_PARAMS = ['preview-align', 'patch-align'];

/** How long the upload field stays outlined after we point the customer at it. */
const ARTWORK_HIGHLIGHT_DURATION = 2400;

/**
 * Runs a callback when the browser is idle, falling back to a timeout.
 *
 * @param {() => void} callback - Work to run.
 * @param {number} timeout - Maximum delay.
 * @returns {number}
 */
function whenIdle(callback, timeout) {
  const idle = /** @type {typeof window.requestIdleCallback | undefined} */ (
    /** @type {any} */ (window).requestIdleCallback
  );

  if (idle) return idle(callback, { timeout });

  return window.setTimeout(callback, timeout);
}

/**
 * The element positioned over the product image. It is created in JavaScript
 * and moved into the gallery, so it never interferes with server-rendered
 * markup or the gallery's own morphing.
 */
export class PatchPreviewOverlay extends HTMLElement {}

if (!customElements.get('patch-preview-overlay')) {
  customElements.define('patch-preview-overlay', PatchPreviewOverlay);
}

/**
 * @typedef {object} PatchDesignerRefs
 * @property {HTMLScriptElement} [configData] - JSON configuration payload.
 * @property {HTMLElement} [errors] - Error region.
 * @property {HTMLElement} [errorHeading] - Focusable error heading.
 * @property {HTMLElement} [errorMessage] - Error text.
 * @property {HTMLElement} [previewUnavailableNotice] - Explains why a selected material has no live preview.
 * @property {HTMLInputElement} [artworkInput] - Customer file input.
 * @property {HTMLInputElement} [artworkSubmissionInput] - Validated artwork file posted with the product form.
 * @property {HTMLInputElement} [contourMarginInput] - Customer Contour border control.
 * @property {HTMLOutputElement} [contourMarginValue] - Current Contour border value.
 * @property {HTMLElement} [artworkFileName] - Selected file name output.
 * @property {HTMLButtonElement} [artworkChoose] - Themed button that opens the file picker.
 * @property {HTMLButtonElement} [artworkRemove] - Removes customer artwork, not a shape-owned pattern.
 * @property {HTMLElement} [artworkStatus] - Polite live region for Contour upload prompts.
 * @property {HTMLElement} [artworkControls] - Positioning controls wrapper.
 * @property {HTMLInputElement} [propPlacement] - Selected physical patch placement.
 * @property {HTMLInputElement} [propShape] - Hidden shape property.
 * @property {HTMLInputElement} [propPrimary] - Hidden patch material property.
 * @property {HTMLInputElement} [propColor] - Hidden patch color property, used when materials are grouped.
 * @property {HTMLInputElement} [propMode] - Hidden artwork method property.
 * @property {HTMLInputElement} [propEngraving] - Hidden engraving color property.
 * @property {HTMLInputElement} [propDieCut] - Hidden die-cut property.
 * @property {HTMLInputElement} [propDesign] - Hidden selected design property.
 * @property {HTMLInputElement} [propPayload] - Hidden JSON payload property.
 * @property {HTMLInputElement} [mockupInput] - Generated PNG file input.
 * @property {HTMLInputElement} [vectorInput] - Generated SVG file input.
 * @property {HTMLElement[]} [groups] - Control groups that can be hidden.
 * @property {HTMLScriptElement} [materialsData] - Material library payload.
 * @property {HTMLScriptElement} [advancedData] - Merchant JSON overrides.
 * @property {HTMLElement} [shapeOptions] - Container for shape swatches.
 * @property {HTMLSelectElement} [materialSetSelect] - Material set value, kept as a real form control.
 * @property {HTMLElement} [materialSetControl] - Wrapper around the material set dropdown.
 * @property {HTMLButtonElement} [materialSetButton] - Button that opens the material set menu.
 * @property {HTMLElement} [materialSetValue] - Shows the selected set on the button.
 * @property {HTMLElement} [materialSetMenu] - The material set listbox.
 * @property {HTMLElement} [materialLegend] - Legend above the material swatches.
 * @property {HTMLElement} [primaryOptions] - Container for primary material swatches.
 * @property {HTMLElement} [engravingOptions] - Container for engraving color swatches.
 *
 * @extends Component<PatchDesignerRefs>
 */
class PatchDesigner extends Component {
  /** @type {import('./patch-config.js').PatchConfig | null} */
  #config = null;

  /** @type {PatchState | null} */
  #store = null;

  /** @type {any} */
  #remoteRenderer = null;

  /** @type {PatchPreviewOverlay | null} */
  #overlay = null;

  /** True once remote setup finished (success or permanent give-up). */
  #previewReady = false;

  /** How many times we have entered remote setup this element life. */
  #remoteSetupAttempts = 0;

  /** @type {import('./patch-artwork.js').ArtworkSource | null} */
  #artwork = null;

  /**
   * Bitmap sent to remote preview. May be a CAD pattern alone, a customer
   * upload alone, or a flatten of both (remote only has one artwork layer).
   *
   * @type {import('./patch-artwork.js').ArtworkSource | null}
   */
  #displayArtwork = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #snapshotTimer = null;

  /** @type {number | null} */
  #contourTraceTimer = null;

  #contourMargin = 3;
  #contourDetail = 'balanced';

  /** @type {string | null} Shape to restore when a gallery design temporarily forces Contour. */
  #shapeBeforeDesignContour = null;

  /** @type {number} */
  #snapshotToken = 0;

  /** @type {Map<string, import('./patch-shapes.js').PatchShape>} */
  #customShapes = new Map();

  /**
   * Compatibility shapes for the hosted renderer. Patterned shapes use a
   * full-silhouette artwork canvas there because that renderer has only one
   * artwork layer; the real shapes retain their customer safe areas.
   *
   * @type {Map<string, import('./patch-shapes.js').PatchShape>}
   */
  #remoteCustomShapes = new Map();

  /** @type {number} */
  #artworkToken = 0;

  /** @type {number} */
  #displayToken = 0;

  /** @type {Map<number, PointerEvent>} */
  #pointers = new Map();

  /** @type {{ x: number, y: number, distance: number, scale: number, patchSize: number } | null} */
  #dragOrigin = null;

  /** Degrees the overlay is tilted by on the current photo. */
  #overlayRotation = 0;

  /** @type {{ destroy: () => void, syncToMedia: () => void } | null} */
  #alignmentEditor = null;

  /** Unique suffix so radio groups never collide with another block on the page. */
  #uid = Math.random().toString(36).slice(2, 8);

  /** Identifies the engraving options on screen so they rebuild only when they change. */
  #engravingSignature = '';

  /** Set the material swatches on screen belong to. Null until they are first built. */
  #materialSetSignature = /** @type {string | null} */ (null);

  #condition = null;
  #placementObserver = null;
  #placementMutation = null;
  #placementStyle = '';
  #placementSignature = '';
  #physicalFitFailed = false;
  #pendingArtworkUpload = null;
  #artworkPackagingFailed = false;

  #bindCondition() {
    this.#condition?.destroy();
    this.#condition = bindPatchCondition(this, (visible) => {
      this.#clearError();
      this.#clearGeneratedFiles();
      if (visible && this.#store) this.#syncProperties(this.#store.state);
      this.#handleOverlayAttached(this.#remoteRenderer?.mountTarget ?? null);
      if (visible) this.#scheduleSnapshot();
      this.refs.propPrimary?.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.#customShapes.clear();
    this.#remoteCustomShapes.clear();
    this.#materialSetSignature = null;
    this.#engravingSignature = '';

    const config = this.#readConfig();
    if (!config) {
      this.#bindCondition();
      return;
    }

    this.#config = config;
    this.#contourMargin = config.dieCut.margin;
    this.#contourDetail = 'balanced';
    this.#shapeBeforeDesignContour = null;
    this.#store = new PatchState(buildInitialState(config, createDefaultState()));

    this.#populateControls();
    this.#syncControlsFromState(this.#store.state);
    void this.#setupRemotePreview();
    this.#bindEvents();
    this.#syncFromDesignPicker({ silent: true });

    this.#store.subscribe((state, changes) => this.#handleStateChange(state, changes));
    this.#handleStateChange(this.#store.state, new Set(['*']));
    this.#bindCondition();

    // Warm textures only if remote preview may load; harmless if omitted.
    if (this.dataset.livePreviewDisabled !== 'true') {
      whenIdle(() => textureCache.preload(collectTextureUrls(config.materials)), 2000);
    }

    if (config.customShapes.length > 0) void this.#loadCustomShapes();
  }

  /**
   * Traces the merchant's uploaded shapes and folds them into the library.
   *
   * The controls are built before this finishes so the configurator is usable
   * straight away; a merchant shape simply gains its outline a moment later. If
   * a shape is already selected when it arrives, the preview is redrawn.
   */
  async #loadCustomShapes() {
    const config = this.#config;
    if (!config) return;

    const { loadCustomShapes } = await import('@patch/svg-shape');
    const { shapes, warnings } = await loadCustomShapes(config.customShapes);

    if (!this.isConnected) {
      if (warnings.length > 0) console.warn('[patch-designer]', warnings.join(' '));
      return;
    }

    for (const shape of shapes) {
      this.#customShapes.set(shape.id, shape);
      this.#remoteCustomShapes.set(
        shape.id,
        shape.engravingArtworkUrl
          ? { ...shape, safeArea: { x: 0, y: 0, width: shape.width, height: shape.height } }
          : shape
      );
    }
    if (warnings.length > 0) console.warn('[patch-designer]', warnings.join(' '));

    const loadedIds = new Set(shapes.map((shape) => shape.id));
    const failedIds = new Set(
      config.customShapes
        .map((shape) => shape.id)
        .filter((id) => !loadedIds.has(id) && !getShape(id, this.#customShapes))
    );

    config.shapes = config.shapes.filter((id) => !failedIds.has(id));
    if (!config.shapes.some((id) => getShape(id, this.#customShapes))) {
      config.shapes = [...PRESET_SHAPE_IDS];
    }

    const fallbackShape = config.shapes.find((id) => getShape(id, this.#customShapes));
    if (fallbackShape && !getShape(config.defaultShape, this.#customShapes)) {
      config.defaultShape = fallbackShape;
    }

    const state = this.#store?.state;
    if (!state) return;

    if (!getShape(state.shape, this.#customShapes)) {
      this.#store?.update({ shape: config.defaultShape, isDieCut: false });
    }

    const currentState = this.#store?.state;
    if (!currentState) return;

    this.#populateControls();
    this.#syncControlsFromState(currentState);
    this.#handleStateChange(currentState, new Set(['*']));
    void this.#syncShapeEngraving(currentState.shape);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#placementObserver?.disconnect();
    this.#placementObserver = null;
    this.#placementMutation?.disconnect();
    this.#placementMutation = null;
    this.#placementSignature = '';
    this.#condition?.destroy();

    this.#controller?.abort();
    this.#controller = null;

    if (this.#snapshotTimer !== null) clearTimeout(this.#snapshotTimer);
    if (this.#contourTraceTimer !== null) clearTimeout(this.#contourTraceTimer);
    this.#contourTraceTimer = null;
    this.#snapshotToken += 1;
    this.#artworkToken += 1;

    if (this.#overlay) clearPerspectiveSurface(this.#overlay);
    this.#remoteRenderer?.destroy?.();
    this.#remoteRenderer = null;
    this.#previewReady = false;
    this.#remoteSetupAttempts = 0;

    this.#alignmentEditor?.destroy();
    this.#alignmentEditor = null;

    if (this.#overlay?.isConnected) this.#overlay.remove();
    this.#overlay = null;

    if (this.#displayArtwork !== this.#artwork) releaseArtworkSource(this.#displayArtwork);
    this.#displayArtwork = null;
    releaseArtworkSource(this.#artwork);
    this.#artwork = null;
  }

  /** @returns {import('./patch-state.js').PatchStateShape | null} */
  get state() {
    return this.#store?.state ?? null;
  }

  /**
   * Makes the current customization safe to serialize into the product form.
   *
   * @returns {Promise<boolean>}
   */
  async prepareForSubmit() {
    this.#condition?.evaluate();
    if (this.dataset.patchConditionKey && this.hidden) return true;
    while (this.#pendingArtworkUpload) await this.#pendingArtworkUpload;
    if (this.#artworkPackagingFailed) return false;
    const config = this.#config;
    const store = this.#store;

    if (!config || !store) {
      this.#showError(
        this.dataset.errorConfig ||
          'This product is not set up for live preview yet. Please contact us and we will help with your order.'
      );
      return false;
    }

    if (config.requireMaterialGroupChoice && !getMaterialGroup(config, store.state.materialGroup)) {
      this.refs.materialSetButton?.setAttribute('aria-invalid', 'true');
      this.#showError(this.dataset.errorMaterialGroup || 'Choose a patch material before adding this product to your cart.');
      return false;
    }

    const selectedGroup = getMaterialGroup(config, store.state.materialGroup);
    if (
      selectedGroup &&
      !selectedGroup.previewEnabled &&
      !getMaterialOption(selectedGroup, store.state.materialOption)
    ) {
      const firstOption = this.querySelector('[data-patch-control="material-option"]');
      if (firstOption instanceof HTMLInputElement) {
        firstOption.setAttribute('aria-invalid', 'true');
        firstOption.focus();
      }
      this.#showError(
        this.dataset.errorMaterialOption ||
          `Choose a ${selectedGroup.name} option before adding this product to your cart.`
      );
      return false;
    }

    if (config.placements.enabled && selectedGroup?.previewEnabled &&
        (!config.placements.items[store.state.preview.placementId]?.available ||
         (this.dataset.livePreviewDisabled !== 'true' && !this.#physicalFitFailed && !this.#resolvePlacementExportTarget()))) {
      this.#showError(this.dataset.errorPlacement);
      return false;
    }

    if (store.state.shape === CONTOUR_SHAPE_ID && !this.#artwork) {
      this.#promptArtworkForContour();
      return false;
    }

    if (this.dataset.requireArtwork === 'true' && !this.#artwork) {
      this.#showError(
        this.dataset.errorRequireArtwork || 'Please personalize your item to add to cart.'
      );
      this.#highlightArtworkGroup();
      return false;
    }

    if (this.#contourTraceTimer !== null && store.state.shape === CONTOUR_SHAPE_ID) {
      clearTimeout(this.#contourTraceTimer);
      this.#contourTraceTimer = null;
      await this.#rebuildDieCut();
    }

    if (store.state.shape === CONTOUR_SHAPE_ID && !store.state.isDieCut) {
      this.#showError(
        this.dataset.errorContourTrace ||
          "We couldn't trace that artwork. Try a PNG with a clear subject and transparent background."
      );
      return false;
    }

    if (this.dataset.guidedMode === 'true') {
      const guidedError = typeof this.guidedValidate === 'function'
        ? this.guidedValidate()
        : this.dataset.errorConfig;
      if (guidedError) {
        this.#showError(guidedError);
        return false;
      }
      this.#syncProperties(store.state);
    }

    // Generated mockups are optional progressive enhancement. Never block ATC
    // waiting for remote preview export.
    try {
      await this.#flushSnapshot();
    } catch {
      this.#clearGeneratedFiles();
    }

    return true;
  }

  // -- setup ----------------------------------------------------------------

  /**
   * @returns {import('./patch-config.js').PatchConfig | null}
   */
  #readConfig() {
    const materials = parseConfigJson(this.refs.materialsData?.textContent ?? null);
    const blockConfig = parseConfigJson(this.refs.configData?.textContent ?? null);
    const advancedConfig = parseConfigJson(this.refs.advancedData?.textContent ?? null);
    const metafieldConfig = parseConfigJson(this.dataset.metafieldConfig ?? null);

    // Later sources win: the metaobject library is the base, block settings
    // shape it, Advanced JSON adds geometry (not copied placement toggles), and a product metafield has
    // the final say so one block can serve many products.
    const merged = mergePatchConfigSources(materials, blockConfig, advancedConfig, metafieldConfig);
    if (this.dataset.guidedMode === 'true') merged.placements = { enabled: false };
    const { config, errors, warnings } = normalizeConfig(merged);
    const notice = this.querySelector('[data-placement-warning]');
    if (notice && (window.Shopify?.designMode || ALIGNMENT_EDITOR_PARAMS.some(p => new URLSearchParams(window.location.search).has(p)))) {
      notice.textContent = warnings.join(' ' ); notice.hidden = !warnings.length;
    }

    for (const warning of warnings) console.warn(`[patch] ${warning}`);

    if (errors.length > 0) {
      console.error('[patch] configuration errors', errors);
      this.#showError(
        this.dataset.errorConfig ||
          'This product is not set up for live preview yet. Please contact us and we will help with your order.'
      );
      this.setAttribute('data-patch-unavailable', 'true');
      return null;
    }

    return config;
  }

  /**
   * Builds the option lists from the configuration.
   *
   * Shape geometry and the material library both live in the engine, so the
   * markup ships empty containers and they are filled here rather than being
   * duplicated in Liquid.
   */
  #populateControls() {
    this.#populatePlacements();
    const config = this.#config;
    if (!config) return;

    const shapeContainer = this.refs.shapeOptions;

    if (shapeContainer) {
      const shapes = getShapes(
        config.shapes.filter((id) => id !== CONTOUR_SHAPE_ID),
        this.#customShapes
      );

      // Contour is offered as a shape swatch when die cut is allowed. It is not
      // a geometric preset: selecting it traces the patch from the artwork.
      if (config.dieCut.allowed) {
        const contour = getShape(CONTOUR_SHAPE_ID);

        if (contour) {
          shapes.push({
            ...contour,
            name: this.dataset.contourLabel || contour.name,
          });
        }
      }

      shapeContainer.replaceChildren(
        ...shapes.map((shape) => {
          const preview = `<svg viewBox="0 0 ${shape.width} ${shape.height}" aria-hidden="true" focusable="false"><path d="${shape.outline}"/></svg>`;
          return this.#buildSwatch({
            control: 'shape',
            group: 'shape',
            value: shape.id,
            name: shape.name,
            visual: preview,
          });
        })
      );
    }

    // The swatches themselves belong to whichever set is selected, so they are
    // left to `#reconcileMaterials` and rebuilt whenever that changes.
    const setSelect = this.refs.materialSetSelect;

    if (setSelect) {
      setSelect.replaceChildren(
        ...[
          ...(config.requireMaterialGroupChoice
            ? [{ id: '', name: this.dataset.materialChoose || 'Choose a patch material' }]
            : []),
          ...config.materialGroups,
        ].map((group) => {
          const option = document.createElement('option');
          option.value = group.id;
          option.textContent = group.id ? this.#materialGroupDisplayName(group) : group.name;
          return option;
        })
      );
    }

    this.refs.materialSetMenu?.replaceChildren(
      ...config.materialGroups.map((group) => {
        const option = document.createElement('li');

        option.className = 'patch-designer__select-option';
        option.role = 'option';
        option.tabIndex = -1;
        option.dataset.value = group.id;
        option.textContent = this.#materialGroupDisplayName(group);

        return option;
      })
    );

    const legend = this.refs.materialLegend;

    if (
      legend &&
      (config.materialGroups.length > 1 || config.materialGroups.some((group) => !group.previewEnabled)) &&
      legend.dataset.groupedLabel
    ) {
      legend.textContent = legend.dataset.groupedLabel;
    }

    // A production method the merchant has switched off should not be offered.
    for (const [mode, allowed] of [
      ['engraved', config.artwork.allowEngraved],
      ['uv-print', config.artwork.allowUvPrint],
    ]) {
      const option = this.querySelector(`[data-patch-mode="${mode}"]`);
      if (option instanceof HTMLElement) option.hidden = !allowed;
    }
  }

  /**
   * Rebuilds the material swatches for the set currently selected.
   *
   * Which set that is comes from the selected material rather than from state
   * of its own, so the picker and the swatches cannot disagree about which set
   * is showing.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #reconcileMaterials(state) {
    const config = this.#config;
    const container = this.refs.primaryOptions;
    if (!config || !container) return;

    const group = getMaterialGroup(config, state.materialGroup);
    const setId = group?.previewEnabled ? group.materialSet : null;
    const signature = group?.id ?? '';
    if (signature === this.#materialSetSignature) return;

    this.#materialSetSignature = signature;

    if (!group) {
      container.replaceChildren();
      this.#syncMaterialSetControl('');
      return;
    }

    if (!group.previewEnabled) {
      container.replaceChildren(
        ...group.options.map((option) =>
          this.#buildSwatch({
            control: 'material-option',
            group: 'material-option',
            value: option.id,
            name: option.name,
            style: `--swatch-color: ${option.fallbackColor || '#e9ecef'};${
              option.swatchImage ? ` --swatch-image: url("${option.swatchImage}");` : ''
            }`,
            tooltip: true,
          })
        )
      );

      for (const input of container.querySelectorAll('[data-patch-control="material-option"]')) {
        if (input instanceof HTMLInputElement) input.checked = input.value === state.materialOption;
      }

      this.#syncMaterialSetControl(group.id);
      return;
    }

    container.replaceChildren(
      ...getPatchMaterials(config, setId).map((material) =>
        this.#buildSwatch({
          control: 'primary',
          group: 'primary',
          value: material.id,
          name: material.name,
          // The swatch shows the material itself, so customers judge grain and
          // sheen rather than a flat approximation of the color.
          style: `--swatch-color: ${material.fallbackColor};${
            material.swatchImage ? ` --swatch-image: url("${material.swatchImage}");` : ''
          }`,
        })
      )
    );

    // Freshly built radios start unchecked, so reflect the stored material.
    for (const input of container.querySelectorAll('[data-patch-control="primary"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === state.materials.primary;
    }

    this.#syncMaterialSetControl(group.id);
  }

  /**
   * Points the dropdown at a set.
   *
   * The button carries the current value as its own text, so it has to be told
   * what that value is whenever the set changes underneath it — including when
   * the change came from somewhere other than the dropdown, such as a saved
   * configuration being restored.
   *
   * @param {string} setId - Set currently in effect.
   */
  #syncMaterialSetControl(groupId) {
    const select = this.refs.materialSetSelect;
    if (select && select.value !== groupId) select.value = groupId;

    for (const option of this.refs.materialSetMenu?.children ?? []) {
      if (option instanceof HTMLElement) option.ariaSelected = String(option.dataset.value === groupId);
    }

    const value = this.refs.materialSetValue;
    const group = this.#config?.materialGroups.find((entry) => entry.id === groupId);
    const name = group
      ? this.#materialGroupDisplayName(group)
      : this.dataset.materialChoose || 'Choose a patch material';

    if (value && value.textContent !== name) value.textContent = name;
  }

  /**
   * @param {import('./patch-config.js').PatchMaterialGroup} group
   * @returns {string}
   */
  #materialGroupDisplayName(group) {
    const suffix = this.#config?.materialGroupFeeLabels[group.name.toLowerCase()] ?? '';
    return `${group.name}${suffix}`;
  }

  /**
   * Opens or closes the material set menu.
   *
   * @param {boolean} open - Whether the menu should be showing.
   */
  #toggleMaterialSetMenu(open) {
    const menu = this.refs.materialSetMenu;
    const button = this.refs.materialSetButton;
    if (!menu || !button) return;

    menu.hidden = !open;
    button.ariaExpanded = String(open);

    if (!open) return;

    // Opening moves focus into the list so the arrow keys work straight away,
    // starting from the current value rather than the top of the list.
    const selected = menu.querySelector('[aria-selected="true"]') ?? menu.firstElementChild;
    if (selected instanceof HTMLElement) selected.focus();
  }

  /**
   * Keyboard handling for the material set menu.
   *
   * @param {KeyboardEvent} event
   */
  #handleMaterialSetKeydown(event) {
    const menu = this.refs.materialSetMenu;
    const button = this.refs.materialSetButton;
    if (!menu || !button) return;

    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.refs.materialSetControl?.contains(target)) return;

    if (target === button) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();
      this.#toggleMaterialSetMenu(true);
      return;
    }

    const options = /** @type {HTMLElement[]} */ ([...menu.children]);
    const index = options.indexOf(target);

    switch (event.key) {
      case 'Escape':
        this.#toggleMaterialSetMenu(false);
        button.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        options[Math.min(index + 1, options.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        options[Math.max(index - 1, 0)]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#chooseMaterialSet(target);
        break;
      default:
        break;
    }
  }

  /**
   * Applies the set an option represents and closes the menu.
   *
   * @param {HTMLElement} option - The chosen `role="option"` element.
   */
  #chooseMaterialSet(option) {
    const setId = option.dataset.value;
    if (!setId) return;

    this.#selectMaterialSet(setId);
    this.#syncMaterialSetControl(setId);
    this.#toggleMaterialSetMenu(false);
    this.refs.materialSetButton?.focus();
  }

  /**
   * Moves the selection to the first material in a set.
   *
   * @param {string} setId - Set the customer picked.
   */
  #selectMaterialSet(groupId) {
    const config = this.#config;
    const store = this.#store;
    if (!config || !store) return;

    const group = getMaterialGroup(config, groupId);
    if (!group) return;

    this.refs.materialSetButton?.removeAttribute('aria-invalid');
    this.#clearError();

    if (!group.previewEnabled) {
      const currentOption =
        store.state.materialGroup === group.id ? getMaterialOption(group, store.state.materialOption) : null;
      store.update({ materialGroup: group.id, materialOption: currentOption?.id ?? null });
      return;
    }

    const currentSet = getMaterialSetId(config, store.state.materials.primary);
    const [first] = getPatchMaterials(config, group.materialSet);
    store.update({
      materialGroup: group.id,
      materialOption: null,
      ...(currentSet === group.materialSet || !first ? {} : { materials: { primary: first.id } }),
    });
  }

  /**
   * Rebuilds the engraving swatches for the material currently selected, and
   * corrects the stored color when the previous choice is impossible on it.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   * @returns {boolean} True when the color was corrected and a fresh state pass is already running.
   */
  #reconcileEngraving(state) {
    const config = this.#config;
    const store = this.#store;
    if (!config || !store) return false;

    const { colors, fixed, material } = getEngravingOptions(config, state.materials.primary);
    const signature = `${material?.id ?? ''}|${fixed}|${colors.map((color) => color.id).join(',')}`;

    if (signature !== this.#engravingSignature) {
      this.#engravingSignature = signature;

      const container = this.refs.engravingOptions;

      if (container) {
        container.replaceChildren(
          ...colors.map((color) =>
            this.#buildSwatch({
              control: 'engraving',
              group: 'engraving',
              value: color.value,
              name: color.name,
              style: `--swatch-color: ${color.value};`,
            })
          )
        );

        // With only one possible result there is nothing to choose, so the
        // swatch list gives way to a plain statement of what will happen.
        container.hidden = fixed;
      }

      const note = this.refs.engravingNote;

      if (note instanceof HTMLElement) {
        note.hidden = !fixed;
        note.textContent = fixed
          ? (note.dataset.template ?? 'Engraves to [color].')
              .replace('[color]', colors[0]?.name ?? '')
              .replace('[material]', material?.name ?? '')
          : '';
        note.style.setProperty('--patch-engraving-color', colors[0]?.value ?? '#141414');
      }

      // Freshly built radios start unchecked, so reflect the stored color.
      for (const input of this.querySelectorAll('[data-patch-control="engraving"]')) {
        if (input instanceof HTMLInputElement) input.checked = input.value === state.artwork.engravingColor;
      }
    }

    const allowed = colors.some((color) => color.value === state.artwork.engravingColor);
    const [first] = colors;

    if (!allowed && first) {
      store.update({ artwork: { engravingColor: first.value } });
      return true;
    }

    return false;
  }

  /**
   * @param {object} options - Swatch options.
   * @param {string} options.control - Value for `data-patch-control`.
   * @param {string} options.group - Radio group suffix.
   * @param {string} options.value - Input value.
   * @param {string} options.name - Visible label.
   * @param {string} [options.style] - Inline custom properties for the visual.
   * @param {string} [options.visual] - Inline markup rendered inside the visual.
   * @param {boolean} [options.tooltip] - Whether the swatch has a hover/focus and mobile tap tooltip.
   * @returns {HTMLElement}
   */
  #buildSwatch({ control, group, value, name, style = '', visual = '', tooltip = false }) {
    const label = document.createElement('label');
    label.className = 'patch-designer__swatch';
    if (tooltip) label.classList.add('patch-designer__swatch--tooltip');

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = `patch-${group}-${this.#uid}`;
    input.value = value;
    input.dataset.patchControl = control;

    const swatch = document.createElement('span');
    swatch.className = 'patch-designer__swatch-visual';
    if (style) swatch.setAttribute('style', style);
    if (visual) swatch.innerHTML = visual;

    const caption = document.createElement('span');
    caption.className = 'patch-designer__swatch-name';
    caption.textContent = name;

    label.append(input, swatch, caption);

    if (tooltip) {
      const tooltipContent = document.createElement('span');
      tooltipContent.className = 'patch-designer__swatch-tooltip';
      tooltipContent.role = 'tooltip';

      const tooltipImage = document.createElement('span');
      tooltipImage.className = 'patch-designer__swatch-tooltip-image';
      if (style) tooltipImage.setAttribute('style', style);

      const tooltipLabel = document.createElement('span');
      tooltipLabel.className = 'patch-designer__swatch-tooltip-label';
      tooltipLabel.textContent = name;
      tooltipContent.append(tooltipImage, tooltipLabel);
      label.append(tooltipContent);

      label.addEventListener('click', (event) => {
        if (!window.matchMedia('(max-width: 749px)').matches || event.target instanceof HTMLInputElement) return;

        const wasOpen = tooltipContent.dataset.open === 'true';
        for (const openTooltip of this.querySelectorAll('.patch-designer__swatch-tooltip[data-open="true"]')) {
          if (!(openTooltip instanceof HTMLElement)) continue;
          openTooltip.style.display = 'none';
          openTooltip.dataset.open = 'false';
        }
        if (!wasOpen) {
          tooltipContent.style.display = 'block';
          tooltipContent.dataset.open = 'true';
        }
      });
    }

    return label;
  }

  /**
   * Reflects the initial state into the controls so the markup and the store
   * agree before the customer touches anything.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #syncControlsFromState(state) {
    /** @type {Record<string, string>} */
    const values = {
      // Contour stays selected while die cut is active, even though the live
      // silhouette is the traced artwork rather than the Contour placeholder.
      shape: state.isDieCut || state.shape === CONTOUR_SHAPE_ID ? CONTOUR_SHAPE_ID : state.shape,
      primary: state.materials.primary ?? '',
      'material-option': state.materialOption ?? '',
      engraving: state.artwork.engravingColor ?? '',
      mode: state.artwork.mode,
    };

    for (const [control, value] of Object.entries(values)) {
      for (const input of this.querySelectorAll(`[data-patch-control="${control}"]`)) {
        if (!(input instanceof HTMLInputElement)) continue;
        input.checked = input.value === value;
      }
    }

    this.#syncPositionInputs(state.artwork.position);
    this.#syncPreviewAdjustmentInputs(state.preview.adjustment);
    if (this.refs.contourMarginInput) {
      this.refs.contourMarginInput.max = String(Math.max(20, Math.ceil(this.#contourMargin)));
      this.refs.contourMarginInput.value = String(this.#contourMargin);
    }
    if (this.refs.contourMarginValue) this.refs.contourMarginValue.value = `${this.#contourMargin}%`;
    for (const input of this.querySelectorAll('[data-patch-control="contour-detail"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === this.#contourDetail;
    }
  }

  async #setupRemotePreview() {
    const config = this.#config;
    if (this.dataset.livePreviewDisabled === 'true') {
      this.#clearGeneratedFiles();
      this.#previewReady = true;
      return;
    }
    if (!config || !this.isConnected || this.#remoteRenderer) return;

    this.#remoteSetupAttempts += 1;
    const attempt = this.#remoteSetupAttempts;

    try {
      const remote = await createRemotePreviewRenderer({
        domain: this.dataset.previewDomain || '',
        config,
        customShapes: this.#remoteCustomShapes,
      });

      if (!remote || !this.isConnected) {
        this.#clearGeneratedFiles();
        this.#previewReady = true;
        // First grant/module fetch can fail on a cold Worker. Retry once after
        // a short delay so shoppers are not stuck until a full page reload.
        if (attempt < 2) {
          window.setTimeout(() => {
            if (!this.isConnected || this.#remoteRenderer) return;
            void this.#setupRemotePreview();
          }, 1500);
        } else {
          this.#setCanvasUnavailable(true);
        }
        return;
      }

      const overlay = /** @type {PatchPreviewOverlay} */ (document.createElement('patch-preview-overlay'));
      overlay.className = 'patch-preview-overlay';
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');

      // Assign before mount so PreviewMount's synchronous onAttach can unhide.
      this.#overlay = overlay;

      const galleryRoot = this.#findGalleryRoot();
      await remote.mount(overlay, this.#store?.state, {
        galleryRoot,
        externalPlacement: true,
        getMediaId: () => this.#store?.state.preview.mediaId ?? null,
        onAttach: (target) => this.#handleOverlayAttached(target),
      });

      if (!this.isConnected) {
        remote.destroy();
        this.#overlay = null;
        return;
      }

      this.#remoteRenderer = remote;
      if (this.#controller) this.#bindDragging(overlay, this.#controller.signal);
      if (config.placements.enabled) {
        this.#placementObserver?.disconnect();
        this.#placementObserver = new ResizeObserver(() => this.#handleOverlayAttached(remote.mountTarget ?? null));
        this.#placementObserver.observe(overlay);
        this.#placementMutation = new MutationObserver(() => {
          if (!this.#alignmentEditor && overlay.style.cssText !== this.#placementStyle) this.#handleOverlayAttached(remote.mountTarget ?? null);
        });
        this.#placementMutation.observe(overlay, { attributes:true, attributeFilter:['style'] });
      }
      this.#setCanvasUnavailable(false);
      if (this.#store) {
        remote.update(this.#stateForRemote(this.#store.state));
        this.#handleOverlayAttached(remote.mountTarget ?? null);
      }
      void this.#syncDisplayArtwork();
      this.#previewReady = true;

      const params = new URLSearchParams(window.location.search);
      if (ALIGNMENT_EDITOR_PARAMS.some((param) => params.has(param))) {
        void this.#loadAlignmentEditor();
      }
    } catch (error) {
      if (new URLSearchParams(window.location.search).has('patch-preview-debug')) {
        console.info('[patch-preview-remote] setup failed', error);
      }
      this.#clearGeneratedFiles();
      this.#previewReady = true;
      this.#setCanvasUnavailable(true);
    }
  }

  #setCanvasUnavailable(unavailable) {
    if (this.dataset.guidedMode !== 'true') return;
    const section = this.closest('.shopify-section');
    const status = section?.querySelector('[data-patch-canvas-status]');
    if (status instanceof HTMLElement) status.hidden = !unavailable;
  }

  /**
   * @returns {Element | null}
   */
  #findGalleryRoot() {
    const selector = this.dataset.gallerySelector || 'media-gallery';
    const section = this.closest('.shopify-section') ?? document;

    return section.querySelector(selector) ?? document.querySelector(selector);
  }

  /**
   * @param {Element | null} target
   */
  #populatePlacements() {
    const container = this.querySelector('[data-placement-options]');
    const config = this.#config;
    if (!container || !config) return;
    container.replaceChildren();
    const ids = PLACEMENT_IDS.filter(id => config.placements.items[id].available);
    for (const id of ids) {
      const label = document.createElement('label');
      label.className = 'patch-designer__placement';
      if (ids.length > 1) {
        const input = document.createElement('input');
        input.type = 'radio'; input.name = `patch-placement-${this.#uid}`;
        input.value = id; input.dataset.patchControl = 'placement';
        input.checked = id === this.#store?.state.preview.placementId;
        label.append(input);
      }
      const graphic = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      graphic.setAttribute('viewBox', PLACEMENT_ICON_VIEWBOX);
      graphic.setAttribute('aria-hidden', 'true'); graphic.setAttribute('focusable', 'false');
      const path = document.createElementNS(graphic.namespaceURI, 'path');
      path.setAttribute('d', PLACEMENT_ICONS[id]);
      path.setAttribute('fill', 'currentColor'); path.setAttribute('fill-rule', 'evenodd');
      graphic.append(path);
      const text = document.createElement('span'); text.textContent = this.dataset[`placement${id === 'front_center' ? 'Center' : id === 'front_left' ? 'Left' : 'Right'}`];
      label.append(graphic, text); container.append(label);
    }
    const error = this.querySelector('[data-placement-error]');
    if (error) error.hidden = !config.placements.enabled || ids.length > 0;
  }

  #basePlacement(target) {
    const alignment = getAlignment(this.#config, describeMedia(target, this.#findGalleryRoot()), this.#store?.state.preview.placementId);
    const state = this.#store?.state;
    const box = this.#overlay?.querySelector('svg')?.viewBox?.baseVal;
    const shape = this.#remoteRenderer?.shape ?? getShape(state?.shape, this.#customShapes);
    const selected = this.guidedState?.()?.size;
    return resolveSizing(alignment, { ...shape, id:state?.shape, ...(box?.width ? {width:box.width,height:box.height} : {}) },
      selected?.width && selected?.height ? {width:selected.width,height:selected.height} : null);
  }

  #placementDimensions(target) {
    const rect = target?.getBoundingClientRect();
    const image = target?.querySelector('img');
    const shape = this.#remoteRenderer?.shape ?? getShape(this.#store?.state.shape, this.#customShapes);
    const svg = this.#overlay?.querySelector('svg');
    const box = svg?.viewBox?.baseVal;
    return { hostAspect: rect?.width && rect.height ? rect.width / rect.height : image?.naturalWidth / image?.naturalHeight || 1,
      patchAspect: box?.width && box.height ? box.width / box.height : shape?.width / shape?.height || 1 };
  }

  #setPreviewAdjustment(proposed) {
    const store = this.#store;
    if (!store) return;
    let next = { ...store.state.preview.adjustment, ...proposed };
    if (this.#config?.placements.enabled) {
      const target = this.#remoteRenderer?.mountTarget;
      if (!target) return;
      const { hostAspect, patchAspect } = this.#placementDimensions(target);
      next = constrainAdjustment(this.#basePlacement(target), next, store.state.preview.adjustment, hostAspect, patchAspect);
      if (!next) return;
    }
    store.update({ preview: { adjustment: next } });
    this.#syncPreviewAdjustmentInputs(next);
  }

  #handleOverlayAttached(target) {
    this.#physicalFitFailed = false;
    if (!target || !this.#overlay || !this.#config) {
      if (!target && this.#config?.placements.enabled) this.#clearGeneratedFiles();
      return;
    }

    const baseAlignment = this.#basePlacement(target);
    const adjustment = this.#store?.state.preview.adjustment ?? { x: 0, y: 0, size: 0 };
    const alignment = adjustedAlignment(baseAlignment, adjustment);

    // Kept so dragging can undo the tilt: the customer moves the artwork along
    // the patch's own axes, not the screen's.
    this.#overlayRotation = alignment.rotation;
    let sizeNotice = this.querySelector('[data-patch-size-status]');
    if (!sizeNotice) {
      sizeNotice = document.createElement('p'); sizeNotice.dataset.patchSizeStatus = ''; sizeNotice.setAttribute('role','status');
      sizeNotice.className = 'patch-designer__notice'; this.append(sizeNotice);
    }
    const sizeKey = alignment.sizeStatus === 'calibrated' ? 'sizeCalibrated' : alignment.sizeStatus === 'incompatible' ? 'sizeIncompatible' : 'sizeApproximate';
    sizeNotice.textContent = sizeNotice.dataset[sizeKey.replace('size','').toLowerCase()] || this.dataset[sizeKey] || ({sizeCalibrated:'Calibrated preview size.',sizeIncompatible:'Preview size is approximate: selected dimensions do not match this shape.',sizeApproximate:'Approximate preview size.'})[sizeKey];
    sizeNotice.hidden = !alignment.sizingMode || alignment.sizingMode === 'legacy' || alignment.hide || !this.#isPreviewEnabled();
    const resize = this.querySelector('[data-patch-control="preview-size"]');
    if (resize) { resize.disabled = alignment.sizingMode === 'physical'; resize.setAttribute('aria-disabled',String(resize.disabled)); }


    if (!this.#isPreviewEnabled()) {
      this.#overlay.hidden = true;
      this.#overlay.setAttribute('aria-hidden', 'true');
      return;
    }

    // While the merchant is aligning, the editor owns the overlay. Applying the
    // saved alignment here would throw away the adjustment they just made every
    // time a variant swapped the photo.
    if (this.#alignmentEditor) {
      this.#overlay.hidden = false;
      this.#overlay.setAttribute('aria-hidden', 'false');
      this.#alignmentEditor.syncToMedia();
      return;
    }

    // A photo the patch does not belong on, typically a back or detail shot.
    // Hidden rather than unmounted so swiping back to a front view restores it.
    if (this.#config.placements.enabled && !alignment.hide) {
      const { hostAspect, patchAspect } = this.#placementDimensions(target);
      const constrained = constrainAdjustment(baseAlignment, adjustment, adjustment, hostAspect, patchAspect);
      if (!constrained) {
        alignment.hide = true;
        if (baseAlignment.sizingMode === 'physical') {
          this.#physicalFitFailed = true;
          sizeNotice.hidden = false; sizeNotice.textContent = sizeNotice.dataset.doesNotFit || this.dataset.sizeDoesNotFit || 'Selected size does not fit this placement. Preview omitted; your order size is unchanged.';
          this.#clearGeneratedFiles();
        }
      }
      else if (Object.keys(constrained).some(k => Math.abs(constrained[k] - adjustment[k]) > 1e-7)) {
        this.#store.update({ preview: { adjustment: constrained } });
        this.#syncPreviewAdjustmentInputs(constrained);
        return;
      }
    }
    this.#overlay.hidden = alignment.hide;
    this.#overlay.setAttribute('aria-hidden', alignment.hide ? 'true' : 'false');

    applyAlignment(this.#overlay, alignment);
    this.#placementStyle = this.#overlay.style.cssText;
    const signature = JSON.stringify([target.getAttribute('data-media-id'), alignment]);
    if (this.#config.placements.enabled && this.#store && signature !== this.#placementSignature) {
      this.#placementSignature = signature;
      this.#syncProperties(this.#store.state);
      this.#clearGeneratedFiles();
      this.#scheduleSnapshot();
    }
  }

  #bindEvents() {
    const signal = this.#controller?.signal;
    if (!signal) return;

    this.addEventListener('change', (event) => this.#handleControlEvent(event), { signal });
    this.addEventListener('input', (event) => this.#handleControlEvent(event), { signal });
    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    setupCleanupControls(this, signal);
    this.addEventListener('patch-guided:size-change', () => {
      if (this.#store) this.#remoteRenderer?.update?.(this.#stateForRemote(this.#store.state), {target:this.#remoteRenderer?.mountTarget,galleryRoot:this.#findGalleryRoot()});
      this.#clearGeneratedFiles();
      this.#handleOverlayAttached(this.#remoteRenderer?.mountTarget ?? null);
      this.#scheduleSnapshot();
    }, { signal });
    this.addEventListener('patch-guided:cleanup-change', () => {
      void this.#syncDisplayArtwork();
      if (this.#store) this.#syncProperties(this.#store.state);
      this.#scheduleSnapshot();
    }, { signal });
    this.addEventListener('keydown', (event) => this.#handleMaterialSetKeydown(event), { signal });

    // Clicking anywhere else closes the menu, which is the behaviour every
    // other dropdown on the page has.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const inside = target instanceof Node && this.refs.materialSetControl?.contains(target);

        if (!inside) this.#toggleMaterialSetMenu(false);
        if (!(target instanceof Element) || !target.closest('.patch-designer__swatch--tooltip')) {
          for (const tooltip of this.querySelectorAll('.patch-designer__swatch-tooltip[data-open="true"]')) {
            if (!(tooltip instanceof HTMLElement)) continue;
            tooltip.style.display = 'none';
            tooltip.dataset.open = 'false';
          }
        }
      },
      { signal }
    );

    document.addEventListener('hatlabDesignSelected', () => this.#syncFromDesignPicker({}), { signal });
    document.addEventListener('hatlabDesignCleared', () => this.#syncFromDesignPicker({}), { signal });

    // Variant changes can replace the whole <media-gallery> (Horizon ProductSelect)
    // or morph its contents (older variant:update). Either way the overlay must
    // reattach to the live photo or it disappears with the old node.
    this.#bindGalleryRemount(signal);


  }

  /**
   * Keeps the remote overlay on the current product photo after gallery swaps.
   *
   * @param {AbortSignal} signal
   */
  #bindGalleryRemount(signal) {
    const section = this.closest('.shopify-section');

    /** @type {number | null} */
    let remountFrame = null;

    const remount = () => {
      if (!this.isConnected || !this.#remoteRenderer || !this.#overlay) return;

      const root = this.#findGalleryRoot();
      this.#remoteRenderer.setMountRoot?.(root);
      this.#remoteRenderer.refreshMount?.();
      if (this.#store) {
        this.#remoteRenderer.update?.(this.#stateForRemote(this.#store.state), {
          target: this.#remoteRenderer.mountTarget ?? null,
          galleryRoot: root,
        });
      }
      this.#handleOverlayAttached(this.#remoteRenderer.mountTarget ?? null);
    };

    const scheduleRemount = () => {
      if (remountFrame !== null) cancelAnimationFrame(remountFrame);
      // Two frames: media-gallery replaceWith runs in the ProductSelect promise
      // microtask; wait until the new gallery is in the document.
      remountFrame = requestAnimationFrame(() => {
        remountFrame = requestAnimationFrame(() => {
          remountFrame = null;
          remount();
        });
      });
    };

    signal.addEventListener(
      'abort',
      () => {
        if (remountFrame !== null) cancelAnimationFrame(remountFrame);
      },
      { once: true }
    );

    section?.addEventListener(ThemeEvents.variantUpdate, scheduleRemount, { signal });

    // Horizon: variant picker dispatches shopify:product:select; media-gallery
    // then replaceWith()'s itself. Optional import — Vero may not ship this map.
    void import('@shopify/events')
      .then((mod) => {
        if (signal.aborted) return;
        const eventName = mod.StandardEvents?.productSelect;
        if (!eventName) return;

        /** @param {Event & { promise?: Promise<unknown>, target: EventTarget | null }} event */
        const onProductSelect = (event) => {
          if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
          if (section && !section.contains(event.target) && event.target !== section) {
            // Still allow document-level selects that morph this section's gallery.
          }
          const after = () => scheduleRemount();
          if (event.promise && typeof event.promise.then === 'function') {
            event.promise.then(after).catch(() => {});
          } else {
            after();
          }
        };

        document.addEventListener(eventName, onProductSelect, { signal });
      })
      .catch(() => {});

    if (section) {
      const observer = new MutationObserver(() => {
        if (!this.#overlay || !this.#remoteRenderer) return;
        const root = this.#findGalleryRoot();
        if (!root) return;
        if (!this.#overlay.isConnected || !root.contains(this.#overlay)) {
          scheduleRemount();
        }
      });
      observer.observe(section, { childList: true, subtree: true });
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
  }

  // -- control handling -----------------------------------------------------

  /**
   * @param {Event} event
   */
  #handleControlEvent(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const control = target.dataset.patchControl;
    if (!control || !this.#store) return;

    if (target instanceof HTMLInputElement && target.type === 'file') {
      if (event.type !== 'change') return;
      const pending = this.#handleFileSelected(target);
      this.#pendingArtworkUpload = pending;
      void pending.finally(() => {
        if (this.#pendingArtworkUpload === pending) this.#pendingArtworkUpload = null;
      });
      return;
    }

    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;

    const { value } = target;

    switch (control) {
      case 'placement':
        if (this.#config?.placements.enabled && this.#config.placements.items[value]?.available) {
          this.#clearError();
          this.#store.update({ preview: { placementId: value, adjustment: { x: 0, y: 0, size: 0 } } });
          this.#syncPreviewAdjustmentInputs(this.#store.state.preview.adjustment);
        }
        break;
      case 'shape':
        void this.#selectShape(value);
        break;
      case 'primary':
        this.#store.update({ materials: { primary: value } });
        break;
      case 'material-option':
        for (const input of this.querySelectorAll('[data-patch-control="material-option"]')) {
          input.removeAttribute('aria-invalid');
        }
        this.#clearError();
        this.#store.update({ materialOption: value });
        break;
      case 'material-set':
        this.#selectMaterialSet(value);
        break;
      case 'mode':
        this.#store.update({ artwork: { mode: value === 'uv-print' ? 'uv-print' : 'engraved' } });
        break;
      case 'engraving':
        this.#store.update({ artwork: { engravingColor: value } });
        break;
      case 'contour-margin': {
        if (!this.#config?.dieCut.allowCustomerMargin) break;
        const margin = Number(value);
        if (!Number.isFinite(margin) || margin === this.#contourMargin) break;
        this.#contourMargin = Math.max(0, Math.min(40, margin));
        if (this.refs.contourMarginValue) this.refs.contourMarginValue.value = `${this.#contourMargin}%`;
        this.#syncProperties(this.#store.state);
        this.#scheduleContourTrace();
        break;
      }
      case 'contour-detail':
        if (!this.#config?.dieCut.allowCustomerDetail || !target.checked || value === this.#contourDetail) break;
        if (!['smooth', 'balanced', 'detailed'].includes(value)) break;
        this.#contourDetail = value;
        this.#syncProperties(this.#store.state);
        this.#scheduleContourTrace();
        break;
      case 'position-x':
      case 'position-y':
      case 'position-scale':
      case 'position-rotation':
        this.#updatePosition(control, Number.parseFloat(value));
        break;
      case 'preview-x':
      case 'preview-y':
      case 'preview-size':
        this.#updatePreviewAdjustment(control, Number.parseFloat(value));
        break;
      default:
        break;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (!target.closest('.patch-designer__swatch--tooltip')) {
      for (const tooltip of this.querySelectorAll('.patch-designer__swatch-tooltip[data-open="true"]')) {
        if (!(tooltip instanceof HTMLElement)) continue;
        tooltip.style.display = 'none';
        tooltip.dataset.open = 'false';
      }
    }

    if (this.refs.materialSetButton?.contains(target)) {
      this.#toggleMaterialSetMenu(this.refs.materialSetMenu?.hidden ?? false);
      return;
    }

    const option = target.closest('.patch-designer__select-option');

    if (option instanceof HTMLElement && this.refs.materialSetMenu?.contains(option)) {
      this.#chooseMaterialSet(option);
      return;
    }

    const action = target.closest('[data-patch-action]')?.getAttribute('data-patch-action');
    if (!action) return;

    switch (action) {
      case 'reset-artwork':
        event.preventDefault();
        this.#store?.update({ artwork: { position: { ...DEFAULT_ARTWORK_POSITION } } });
        break;
      case 'reset-preview':
        event.preventDefault();
        this.#setPreviewAdjustment({ x: 0, y: 0, size: 0 });
        break;
      case 'remove-artwork':
        event.preventDefault();
        this.#clearArtwork();
        break;
      case 'upload':
        event.preventDefault();
        this.refs.artworkInput?.click();
        break;
      default:
        break;
    }
  }

  /**
   * @param {string} control
   * @param {number} value
   */
  #updatePosition(control, value) {
    const store = this.#store;
    if (!store || !Number.isFinite(value)) return;
    if (store.state.isDieCut) return;

    const current = store.state.artwork.position;
    /** @type {Partial<import('./patch-state.js').PatchPosition>} */
    const next = {};

    if (control === 'position-x') next.x = value;
    if (control === 'position-y') next.y = value;
    if (control === 'position-scale') next.scale = value;
    if (control === 'position-rotation') next.rotation = value;

    store.update({ artwork: { position: clampPosition(next, current) } });
  }

  /**
   * @param {string} control
   * @param {number} value
   */
  #updatePreviewAdjustment(control, value) {
    const store = this.#store;
    if (!store || !Number.isFinite(value)) return;

    /** @type {Partial<import('./patch-state.js').PatchPreviewAdjustment>} */
    const next = {};

    if (control === 'preview-x') next.x = value;
    if (control === 'preview-y') next.y = value;
    if (control === 'preview-size') next.size = value;

    this.#setPreviewAdjustment(next);
  }

  // -- artwork --------------------------------------------------------------

  /**
   * @param {HTMLInputElement} input
   */
  async #handleFileSelected(input) {
    const config = this.#config;
    const store = this.#store;
    const file = input.files?.[0];

    if (!config || !store || !file) return;

    this.#clearError();

    const token = (this.#artworkToken += 1);
    const result = await loadArtworkFromFile(file, config.artwork, this.#artworkMessages());

    if (token !== this.#artworkToken) {
      if ('source' in result) releaseArtworkSource(result.source);
      return;
    }

    if ('error' in result) {
      this.#showError(result.error);
      input.value = '';
      this.#restoreArtworkFileName();
      return;
    }

    let packaged = null;
    try {
      packaged = await packageArtworkForOrder(file);
    } catch {
      // A raw image fallback would expose production artwork to CDN transforms.
    }
    if (token !== this.#artworkToken || !this.isConnected) {
      releaseArtworkSource(result.source);
      return;
    }
    if (
      !packaged || !this.refs.artworkSubmissionInput ||
      !attachFileToInput(this.refs.artworkSubmissionInput, packaged)
    ) {
      this.#artworkPackagingFailed = true;
      releaseArtworkSource(result.source);
      input.value = '';
      this.#restoreArtworkFileName();
      this.#showError(
        this.dataset.errorFileAttach ||
          "We couldn't attach that artwork to your order. Please choose the file again."
      );
      return;
    }

    this.#artworkPackagingFailed = false;
    const shapeBeforeDesign = store.state.artwork.source === 'design'
      ? this.#shapeBeforeDesignContour
      : null;
    this.#shapeBeforeDesignContour = null;
    this.#setArtwork(result.source);

    if (this.refs.artworkFileName) this.refs.artworkFileName.textContent = result.source.fileName;

    store.update({
      artwork: {
        source: 'upload',
        url: result.source.url,
        fileName: result.source.fileName,
        designId: null,
        designTitle: null,
        naturalWidth: result.source.width,
        naturalHeight: result.source.height,
        isVector: result.source.isVector,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    });

    this.#clearArtworkPrompt();

    if (shapeBeforeDesign && getShape(shapeBeforeDesign, this.#customShapes)) {
      await this.#selectShape(shapeBeforeDesign);
    } else if (store.state.isDieCut || store.state.shape === CONTOUR_SHAPE_ID) {
      await this.#enableContour();
    }
  }

  /** Restores the placeholder the theme rendered, which is the translated one. */
  #resetFileName() {
    const output = this.refs.artworkFileName;
    if (!output) return;

    output.textContent = output.dataset.empty ?? '';
  }

  #restoreArtworkFileName() {
    const output = this.refs.artworkFileName;
    const state = this.#store?.state;
    if (!output || !state) return;

    if (state.artwork.source === 'none' || state.artwork.source === 'shape') {
      output.textContent = output.dataset.empty ?? '';
      return;
    }

    output.textContent = state.artwork.fileName ?? state.artwork.designTitle ?? output.dataset.empty ?? '';
  }

  /** @returns {import('./patch-artwork.js').PatchArtworkMessages} */
  #artworkMessages() {
    return {
      fileEmpty: this.dataset.errorFileEmpty,
      fileType: this.dataset.errorFileType,
      fileSize: this.dataset.errorFileSize,
      fileRead: this.dataset.errorFileRead,
      fileDimensions: this.dataset.errorFileDimensions,
    };
  }

  /**
   * @param {import('./patch-artwork.js').ArtworkSource | null} source
   */
  #setArtwork(source) {
    this.#artworkPackagingFailed = false;
    if (this.#artwork) {
      forgetDataUrl(this.#artwork.url);
      releaseArtworkSource(this.#artwork);
    }

    this.#artwork = source;
  }

  /**
   * Keeps the hosted renderer from applying customer placement a second time
   * to a bitmap that already contains the fixed pattern and positioned upload.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   * @returns {import('./patch-state.js').PatchStateShape}
   */
  #stateForRemote(state) {
    const selected = this.guidedState?.()?.size;
    if (selected?.width && selected?.height) state = { ...state, preview:{...state.preview,selectedSize:{width:selected.width,height:selected.height}} };
    const shape = getShape(state.shape, this.#customShapes);
    if (!shape?.engravingArtworkUrl || state.isDieCut) return state;

    return {
      ...state,
      artwork: {
        ...state.artwork,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    };
  }

  /**
   * @param {import('./patch-artwork.js').ArtworkSource | null} source
   */
  #setRemoteArtwork(source) {
    if (this.#displayArtwork && this.#displayArtwork !== this.#artwork && this.#displayArtwork !== source) {
      releaseArtworkSource(this.#displayArtwork);
    }

    this.#displayArtwork = source;
    this.#remoteRenderer?.setArtwork?.(source);
  }

  /**
   * Pushes the right bitmap to remote preview.
   *
   * Remote preview cannot draw `engravingArtworkUrl` on its own (hosted build
   * is older), so patterned shapes are flattened here: CAD outline full-bleed,
   * customer upload inset on top — upload never replaces the outline.
   */
  async #syncDisplayArtwork() {
    const store = this.#store;
    const token = (this.#displayToken += 1);
    const shape = store ? getShape(store.state.shape, this.#customShapes) : null;
    const engravingUrl = shape?.engravingArtworkUrl;
    const isDieCut = Boolean(store?.state.isDieCut);
    const sourceKind = store?.state.artwork.source;

    let customer = this.#artwork && sourceKind !== 'shape' ? this.#artwork : null;
    const cleanup = readCleanupControls(this) ?? (sourceKind === 'upload' && store?.state.artwork.mode === 'engraved'
        ? { mode: 'smart', color: '#ffffff', tolerance: 28, softness: 2 }
        : null);
    if (customer && cleanup && cleanup.mode !== 'original' && this.#config) {
      const cleaned = await createCleanedPreview(customer, this.#config.artwork, cleanup);
      if (token !== this.#displayToken || !this.isConnected) {
        releaseArtworkSource(cleaned);
        return;
      }
      if (cleaned) customer = cleaned;
    }

    if (!engravingUrl || isDieCut || !shape) {
      this.#setRemoteArtwork(customer);
      return;
    }

    try {
      const display = await buildShapeDisplayArtwork({
        shape,
        engravingUrl,
        customer,
        position: store?.state.artwork.position ?? DEFAULT_ARTWORK_POSITION,
        mode: store?.state.artwork.mode ?? 'engraved',
      });
      if (customer && customer !== this.#artwork) releaseArtworkSource(customer);

      if (token !== this.#displayToken || !this.isConnected) {
        releaseArtworkSource(display);
        return;
      }

      this.#setRemoteArtwork(display);
    } catch (error) {
      if (customer && customer !== this.#artwork) releaseArtworkSource(customer);
      console.warn('[patch-designer]', error);
      if (token === this.#displayToken) this.#setRemoteArtwork(this.#artwork);
    }
  }

  #clearArtwork() {
    const store = this.#store;
    if (!store) return;

    const shapeBeforeDesign = store.state.artwork.source === 'design'
      ? this.#shapeBeforeDesignContour
      : null;
    this.#shapeBeforeDesignContour = null;

    this.#artworkToken += 1;
    this.#displayToken += 1;
    this.#setArtwork(null);

    if (this.refs.artworkInput) this.refs.artworkInput.value = '';
    if (this.refs.artworkSubmissionInput) attachFileToInput(this.refs.artworkSubmissionInput, null);
    this.#resetFileName();

    this.#remoteRenderer?.setDieCutShape?.(null);

    store.update({
      isDieCut: false,
      artwork: {
        source: 'none',
        url: null,
        fileName: null,
        designId: null,
        designTitle: null,
        naturalWidth: 0,
        naturalHeight: 0,
        isVector: false,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    });

    this.#clearArtworkPrompt();
    if (shapeBeforeDesign && getShape(shapeBeforeDesign, this.#customShapes)) {
      void this.#selectShape(shapeBeforeDesign);
    } else {
      void this.#syncShapeEngraving(store.state.shape);
    }
  }

  // -- design picker --------------------------------------------------------

  #designUsesContourOnly() {
    const config = this.#config;
    return Boolean(config?.dieCut.allowed && config.dieCut.designsUseContourOnly && config.artwork.allowDesignPicker);
  }

  /**
   * Adopts whatever the existing design gallery has selected. The design
   * picker owns that state; this only reads it and reacts to its events.
   *
   * @param {object} options - Sync options.
   * @param {boolean} [options.silent] - Skip when no design is selected.
   */
  async #syncFromDesignPicker({ silent = false }) {
    const config = this.#config;
    const store = this.#store;
    // A guided patch is configured for the artwork supplied on this product.
    // Hat Lab's persisted selection can belong to a different product.
    if (this.dataset.guidedMode === 'true') return;
    if (!config || !store || !config.artwork.allowDesignPicker) return;

    const design = window.HATLAB_DESIGN?.getSelectedDesign?.(this);

    if (!design) {
      if (!silent) this.#handleDesignCleared();
      return;
    }

    const url = design.previewImage || design.displayImage || design.thumbnail;
    if (!url) return;

    // Blank metaobject ids used to match every selection (null === null) and
    // skip reloads. Only short-circuit when both a real id and the URL match.
    const designId = design.id != null && design.id !== '' ? design.id : null;
    const loadedUrl = store.state.artwork.url;
    if (
      designId != null &&
      store.state.artwork.designId === designId &&
      loadedUrl === url &&
      this.#artwork
    ) {
      if (this.#designUsesContourOnly() && store.state.shape !== CONTOUR_SHAPE_ID) {
        await this.#selectContour();
      }
      return;
    }

    const token = (this.#artworkToken += 1);
    const result = await loadArtworkSource({
      url,
      config: config.artwork,
      messages: this.#artworkMessages(),
    });

    if (token !== this.#artworkToken) {
      if ('source' in result) releaseArtworkSource(result.source);
      return;
    }

    // A gallery selection can be cleared while its image is still loading.
    // Do not revive that design (and force Contour) after the clear event.
    const selectedNow = window.HATLAB_DESIGN?.getSelectedDesign?.(this);
    const selectedUrl = selectedNow?.previewImage || selectedNow?.displayImage || selectedNow?.thumbnail;
    if (!selectedNow || selectedUrl !== url ||
      (designId != null && String(selectedNow.id) !== String(designId))) {
      if ('source' in result) releaseArtworkSource(result.source);
      return;
    }

    if ('error' in result) {
      this.#showError(
        this.dataset.errorDesign || "We couldn't load that design for preview. Please try another design."
      );
      return;
    }

    if (this.#designUsesContourOnly() && this.#shapeBeforeDesignContour === null &&
      store.state.shape !== CONTOUR_SHAPE_ID) {
      this.#shapeBeforeDesignContour = store.state.shape;
    }

    this.#setArtwork(result.source);

    if (this.refs.artworkInput) this.refs.artworkInput.value = '';
    if (this.refs.artworkSubmissionInput) attachFileToInput(this.refs.artworkSubmissionInput, null);
    if (this.refs.artworkFileName) this.refs.artworkFileName.textContent = design.title ?? '';

    store.update({
      artwork: {
        source: 'design',
        url,
        designId,
        designTitle: design.title ?? null,
        fileName: null,
        naturalWidth: result.source.width,
        naturalHeight: result.source.height,
        isVector: result.source.isVector,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    });

    this.#clearArtworkPrompt();

    if (this.#designUsesContourOnly()) {
      await this.#selectContour();
    } else if (store.state.isDieCut || store.state.shape === CONTOUR_SHAPE_ID) {
      await this.#enableContour();
    }
  }

  #handleDesignCleared() {
    const store = this.#store;
    if (!store || store.state.artwork.source !== 'design') return;

    this.#clearArtwork();
  }

  // -- contour (artwork-shaped cut) -----------------------------------------

  /**
   * Selects a geometric shape or the Contour option.
   *
   * @param {string} value - Shape id, or `contour`.
   */
  async #selectShape(value) {
    const store = this.#store;
    if (!store) return;
    if (this.#designUsesContourOnly() && store.state.artwork.source === 'design' &&
      value !== CONTOUR_SHAPE_ID) return;

    if (this.#contourTraceTimer !== null) clearTimeout(this.#contourTraceTimer);
    this.#contourTraceTimer = null;

    this.#clearError();

    if (value === CONTOUR_SHAPE_ID) {
      await this.#selectContour();
      return;
    }

    this.#clearArtworkPrompt();

    this.#remoteRenderer?.setDieCutShape?.(null);
    store.update({ shape: value, isDieCut: false });
    await this.#syncShapeEngraving(value);
  }

  /**
   * Applies a custom shape's built-in CAD pattern through the artwork channel
   * remote preview understands. Customer uploads stay in `#artwork` and are
   * composited on top — they never replace the outline.
   *
   * @param {string} shapeId - Selected shape id.
   */
  async #syncShapeEngraving(shapeId) {
    const store = this.#store;
    if (!store) return;

    const sourceKind = store.state.artwork.source;
    const shape = getShape(shapeId, this.#customShapes);
    const engravingUrl = shape?.engravingArtworkUrl;

    if (sourceKind === 'upload' || sourceKind === 'design') {
      void this.#syncDisplayArtwork();
      return;
    }

    if (!engravingUrl) {
      if (sourceKind === 'shape') {
        this.#artworkToken += 1;
        this.#displayToken += 1;
        this.#setArtwork(null);
        store.update({
          artwork: {
            source: 'none',
            url: null,
            fileName: null,
            designId: null,
            designTitle: null,
            naturalWidth: 0,
            naturalHeight: 0,
            isVector: false,
            position: { ...DEFAULT_ARTWORK_POSITION },
          },
        });
      } else {
        void this.#syncDisplayArtwork();
      }
      return;
    }

    store.update({
      artwork: {
        source: 'shape',
        mode: 'engraved',
        url: engravingUrl,
        fileName: `${shape?.name || 'Shape'} pattern.png`,
        designId: null,
        designTitle: null,
        naturalWidth: shape.width,
        naturalHeight: shape.height,
        isVector: false,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    });

    if (this.#artwork) {
      forgetDataUrl(this.#artwork.url);
      releaseArtworkSource(this.#artwork);
      this.#artwork = null;
    }

    this.#resetFileName();

    void this.#syncDisplayArtwork();
  }

  /**
   * Activates Contour. Without artwork the upload control is highlighted
   * instead of showing an error; the cut is built once a file arrives.
   */
  async #selectContour() {
    const store = this.#store;
    if (!store) return;

    // Contour cuts to customer artwork, not a shape's built-in pattern.
    if (store.state.artwork.source === 'shape') {
      this.#artworkToken += 1;
      this.#displayToken += 1;
      if (this.#artwork) {
        forgetDataUrl(this.#artwork.url);
        releaseArtworkSource(this.#artwork);
        this.#artwork = null;
      }
      store.update({
        artwork: {
          source: 'none',
          url: null,
          fileName: null,
          designId: null,
          designTitle: null,
          naturalWidth: 0,
          naturalHeight: 0,
          isVector: false,
          position: { ...DEFAULT_ARTWORK_POSITION },
        },
      });
      this.#resetFileName();
    }

    store.update({ shape: CONTOUR_SHAPE_ID, isDieCut: false });

    this.#remoteRenderer?.setDieCutShape?.(null);
    void this.#syncDisplayArtwork();

    if (!this.#artwork) {
      this.#promptArtworkForContour();
      return;
    }

    await this.#enableContour();
  }

  /** Traces the artwork silhouette and switches the preview to that cut. */
  async #enableContour() {
    const store = this.#store;
    if (!store || !this.#artwork) return;

    if (this.#contourTraceTimer !== null) clearTimeout(this.#contourTraceTimer);
    this.#contourTraceTimer = null;

    store.update({ shape: CONTOUR_SHAPE_ID, isDieCut: true });
    await this.#rebuildDieCut();
  }

  async #rebuildDieCut() {
    const config = this.#config;
    const store = this.#store;
    if (!config || !store || !this.#artwork) return;

    const { margin, simplify } = this.#currentContourSettings();
    const result = generateDieCutShape(this.#artwork, { ...config.dieCut, margin, simplify }, {
      read: this.dataset.errorContourRead,
      empty: this.dataset.errorContourEmpty,
      trace: this.dataset.errorContourTrace,
    });

    if ('error' in result) {
      this.#showError(result.error);
      store.update({ shape: CONTOUR_SHAPE_ID, isDieCut: false });

      this.#remoteRenderer?.setDieCutShape?.(null);
      return;
    }

    this.#remoteRenderer?.setDieCutShape?.(result.shape);
    store.update({
      shape: CONTOUR_SHAPE_ID,
      isDieCut: true,
      artwork: { position: { ...DEFAULT_ARTWORK_POSITION } },
    });
  }

  #currentContourSettings() {
    const dieCut = this.#config?.dieCut;
    if (!dieCut) return { margin: 3, detail: 'balanced', simplify: 1.5 };
    const detail = dieCut.allowCustomerDetail ? this.#contourDetail : 'balanced';
    const detailScale = detail === 'detailed' ? 0.5 : detail === 'smooth' ? 2 : 1;
    return {
      margin: dieCut.allowCustomerMargin ? this.#contourMargin : dieCut.margin,
      detail,
      simplify: Math.max(0.5, Math.min(20, dieCut.simplify * detailScale)),
    };
  }

  #scheduleContourTrace() {
    if (this.#contourTraceTimer !== null) clearTimeout(this.#contourTraceTimer);
    this.#contourTraceTimer = window.setTimeout(() => {
      this.#contourTraceTimer = null;
      if (this.isConnected && this.#store?.state.shape === CONTOUR_SHAPE_ID && this.#artwork) {
        void this.#rebuildDieCut();
      }
    }, 350);
  }

  /**
   * Guides the customer to upload artwork when Contour is chosen first.
   * Focus moves to the choose button so keyboard and screen reader users land
   * on the next step without an error banner.
   */
  #promptArtworkForContour() {
    const group = this.#artworkGroup();
    const choose = this.refs.artworkChoose;
    const status = this.refs.artworkStatus;
    const message =
      this.dataset.contourPrompt || 'Add your artwork to cut the patch to its shape.';

    this.#clearError();

    if (!group || group.hidden) {
      this.#showError(message);
      return;
    }

    if (status) {
      status.textContent = message;
      status.hidden = false;
    }

    this.#highlightArtworkGroup();

    requestAnimationFrame(() => {
      choose?.focus();
      group?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  /**
   * Outlines the upload field when checkout is blocked on missing artwork.
   * Focus stays where the caller put it, so the error heading still announces.
   */
  #highlightArtworkGroup() {
    const group = this.#artworkGroup();
    if (!group || group.hidden) return;

    group.classList.add('is-highlighted');
    window.setTimeout(() => group.classList.remove('is-highlighted'), ARTWORK_HIGHLIGHT_DURATION);
  }

  #clearArtworkPrompt() {
    const status = this.refs.artworkStatus;

    this.#artworkGroup()?.classList.remove('is-highlighted');

    if (status) {
      status.textContent = '';
      status.hidden = true;
    }
  }

  /** @returns {HTMLElement | null} */
  #artworkGroup() {
    const group = this.querySelector('[data-patch-group="artwork"]');
    return group instanceof HTMLElement ? group : null;
  }

  // -- rendering ------------------------------------------------------------

  /**
   * @param {import('./patch-state.js').PatchStateShape} state
   * @param {Set<string>} changes
   */
  #handleStateChange(state, changes) {
    this.#clearGeneratedFiles();
    this.#reconcileMaterials(state);

    if (changed(changes, 'materialGroup')) {
      this.#remoteRenderer?.refreshMount?.();
      this.#handleOverlayAttached(this.#remoteRenderer?.mountTarget ?? null);
    }

    // A corrected color re-enters this method with the fixed state, so let that
    // pass do the work rather than rendering and saving a value we just replaced.
    if (this.#reconcileEngraving(state)) return;

    this.#remoteRenderer?.update?.(this.#stateForRemote(state), {
      target: this.#remoteRenderer?.mountTarget ?? null,
      galleryRoot: this.#findGalleryRoot(),
    });

    if (changed(changes, 'artwork') || changed(changes, 'shape') || changed(changes, 'isDieCut')) {
      void this.#syncDisplayArtwork();
    }

    if (changed(changes, 'preview') || changed(changes, 'shape') || changed(changes, 'isDieCut')) {
      this.#handleOverlayAttached(this.#remoteRenderer?.mountTarget ?? null);
    }

    this.#updateControlVisibility(state);
    this.#syncProperties(state);

    if (changed(changes, 'materialGroup') && this.refs.materialSetSelect) {
      this.refs.materialSetSelect.dispatchEvent(new Event('input', { bubbles: true }));
      this.refs.materialSetSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#scheduleSnapshot();
  }

  /**
   * Hides options that do not apply to the current selection, which is what
   * keeps one interface usable across very different products.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #updateControlVisibility(state) {
    const config = this.#config;
    if (!config) return;

    const hasArtwork = state.artwork.source !== 'none';
    const hasCustomerArtwork = state.artwork.source === 'upload' || state.artwork.source === 'design';
    const materialGroup = getMaterialGroup(config, state.materialGroup);
    const hasMaterialGroup = materialGroup != null;
    const previewEnabled = materialGroup?.previewEnabled === true;
    const hideUploadWithDesign =
      state.artwork.source === 'design' && this.#artworkGroup()?.dataset.hideWhenDesign === 'true';
    const designContourOnly = state.artwork.source === 'design' && this.#designUsesContourOnly();

    for (const input of this.refs.shapeOptions?.querySelectorAll('[data-patch-control="shape"]') ?? []) {
      const swatch = input.closest('.patch-designer__swatch');
      if (swatch instanceof HTMLElement) {
        swatch.hidden = designContourOnly && input instanceof HTMLInputElement &&
          input.value !== CONTOUR_SHAPE_ID;
      }
      if (input instanceof HTMLInputElement) input.checked = input.value === state.shape;
    }

    /** @type {Record<string, boolean>} */
    const visibility = {
      placement: hasMaterialGroup && previewEnabled && config.placements.enabled,
      shape: hasMaterialGroup,
      'contour-options': hasMaterialGroup && previewEnabled && config.dieCut.allowed &&
        state.shape === CONTOUR_SHAPE_ID &&
        (config.dieCut.allowCustomerMargin || config.dieCut.allowCustomerDetail),
      'material-set': config.materialGroups.length > 1 || config.requireMaterialGroupChoice,
      primary: hasMaterialGroup && (previewEnabled || materialGroup.options.length > 0),
      mode: hasMaterialGroup && previewEnabled && config.artwork.allowEngraved && config.artwork.allowUvPrint,
      engraving: hasMaterialGroup && previewEnabled && state.artwork.mode === 'engraved',
      artwork: hasMaterialGroup && config.artwork.allowUpload && !hideUploadWithDesign,
      cleanup: this.dataset.livePreviewDisabled !== 'true' && hasMaterialGroup && previewEnabled && hasCustomerArtwork,
      position: this.dataset.livePreviewDisabled !== 'true' && hasMaterialGroup && previewEnabled && hasCustomerArtwork && !state.isDieCut,
      'preview-position': this.dataset.livePreviewDisabled !== 'true' && hasMaterialGroup && previewEnabled && hasArtwork,
    };

    for (const group of this.refs.groups ?? []) {
      const name = group.dataset.patchGroup;
      if (!name || !(name in visibility)) continue;

      const visible = visibility[name] ?? true;

      group.hidden = !visible;
      group.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    for (const input of this.querySelectorAll('[data-patch-control="placement"]')) input.checked = input.value === state.preview.placementId;

    const marginControl = this.querySelector('[data-contour-margin-control]');
    if (marginControl instanceof HTMLElement) marginControl.hidden = !config.dieCut.allowCustomerMargin;
    const detailControl = this.querySelector('[data-contour-detail-control]');
    if (detailControl instanceof HTMLElement) detailControl.hidden = !config.dieCut.allowCustomerDetail;

    // The overlay only intercepts pointers once there is artwork to move,
    // otherwise it would block swiping through the gallery for no reason.
    this.#overlay?.classList.toggle('patch-preview-overlay--draggable', hasCustomerArtwork || state.isDieCut);
    if (this.refs.artworkRemove) this.refs.artworkRemove.hidden = !hasCustomerArtwork;

    if (this.refs.previewUnavailableNotice) {
      this.refs.previewUnavailableNotice.hidden = !hasMaterialGroup || previewEnabled;
    }
  }

  // -- cart data ------------------------------------------------------------

  /**
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #syncProperties(state) {
    if (this.dataset.patchConditionKey && this.hidden) return;
    const config = this.#config;
    if (!config) return;

    const primary = getMaterial(config, state.materials.primary);
    const materialGroup = getMaterialGroup(config, state.materialGroup);
    const materialOption = getMaterialOption(materialGroup, state.materialOption);
    const previewEnabled = materialGroup?.previewEnabled === true;
    const engraving = config.engravingColors.find((color) => color.value === state.artwork.engravingColor);
    const shape = this.#remoteRenderer?.shape;

    // Grouped, the set is the material and the swatch is the color. Ungrouped,
    // the swatch is the material and there is no color to report.
    const grouped = config.materialSets.length > 1 || config.materialGroups.some((group) => !group.previewEnabled);

    const isContour = state.isDieCut || state.shape === CONTOUR_SHAPE_ID;
    const contourLabel = this.dataset.contourLabel || 'Contour';

    setInputValue(this.refs.propPlacement, config.placements.enabled && previewEnabled
      ? this.dataset[`placement${state.preview.placementId === 'front_center' ? 'Center' : state.preview.placementId === 'front_left' ? 'Left' : 'Right'}`] ?? '' : '');
    setInputValue(this.refs.propShape, isContour ? contourLabel : (shape?.name ?? state.shape));
    setInputValue(
      this.refs.propPrimary,
      materialGroup ? (previewEnabled && !grouped ? (primary?.name ?? '') : materialGroup.name) : ''
    );
    setInputValue(
      this.refs.propColor,
      previewEnabled && grouped ? (primary?.name ?? '') : !previewEnabled ? (materialOption?.name ?? '') : ''
    );
    setInputValue(this.refs.propDieCut, state.isDieCut ? 'Yes' : '');
    setInputValue(this.refs.propDesign, state.artwork.designTitle ?? '');
    setInputValue(
      this.refs.propMode,
      !previewEnabled || state.artwork.source === 'none'
        ? ''
        : state.artwork.mode === 'uv-print'
          ? 'Full color UV print'
          : 'Engraved'
    );
    setInputValue(
      this.refs.propEngraving,
      previewEnabled && state.artwork.mode === 'engraved' && state.artwork.source !== 'none'
        ? (engraving?.name ?? '')
        : ''
    );

    // Everything a fulfilment team needs, kept out of the customer's way. A
    // leading underscore hides the property in the cart and at checkout while
    // preserving it on the order.
    setInputValue(
      this.refs.propPayload,
      JSON.stringify({
        v: config.version,
        materialGroup: materialGroup
          ? { id: materialGroup.id, name: materialGroup.name, previewEnabled: materialGroup.previewEnabled }
          : null,
        materialOption: materialOption ? { id: materialOption.id, name: materialOption.name } : null,
        shape: state.isDieCut ? 'die-cut' : state.shape,
        dieCut: state.isDieCut,
        contour: state.isDieCut ? this.#currentContourSettings() : null,
        materials: previewEnabled ? { ...state.materials, set: primary?.set || null } : { primary: null, set: null },
        artwork: {
          source: state.artwork.source,
          mode: previewEnabled ? state.artwork.mode : null,
          engravingColor: previewEnabled ? state.artwork.engravingColor : null,
          fileName: state.artwork.fileName,
          designId: state.artwork.designId,
          previewCleanup: readCleanupControls(this) ?? (state.artwork.source === 'upload' && state.artwork.mode === 'engraved' ? { mode: 'smart' } : null),
          position: previewEnabled ? state.artwork.position : undefined,
        },
        preview: previewEnabled && this.dataset.livePreviewDisabled !== 'true' ? { ...state.preview, ...(config.placements.enabled ? { resolved: this.#resolvedPlacementPayload() } : {}) } : config.placements.enabled && previewEnabled ? { placementId: state.preview.placementId } : null,
        guided: this.dataset.guidedMode === 'true' ? this.guidedState?.() ?? null : null,
      })
    );
  }

  #resolvedPlacementPayload() {
    const resolved = this.#resolvePlacementExportTarget();
    return resolved ? { mediaId: resolved.target.getAttribute('data-media-id'), ...resolved.alignment } : null;
  }

  #scheduleSnapshot() {
    if (!this.refs.mockupInput && !this.refs.vectorInput) return;
    if (!this.#isPreviewEnabled()) {
      this.#clearGeneratedFiles();
      return;
    }
    if (this.#snapshotTimer !== null) clearTimeout(this.#snapshotTimer);

    const token = (this.#snapshotToken += 1);

    this.#snapshotTimer = window.setTimeout(() => {
      this.#snapshotTimer = null;
      whenIdle(() => void this.#generateSnapshot(token), 2000);
    }, SNAPSHOT_DEBOUNCE);
  }

  /** @returns {Promise<boolean>} */
  async #flushSnapshot() {
    if (this.#snapshotTimer !== null) {
      clearTimeout(this.#snapshotTimer);
      this.#snapshotTimer = null;
    }

    const token = (this.#snapshotToken += 1);
    return this.#generateSnapshot(token);
  }

  /**
   * @param {number} token
   * @returns {Promise<boolean>}
   */
  async #generateSnapshot(token) {
    const state = this.#store?.state;
    if (!state) return true;

    if (!this.#isPreviewEnabled() || !this.#remoteRenderer) {
      this.#clearGeneratedFiles();
      return true;
    }

    // Nothing worth attaching until customer artwork or a shape-owned pattern exists.
    if (state.artwork.source === 'none') {
      if (this.refs.mockupInput) attachFileToInput(this.refs.mockupInput, null);
      if (this.refs.vectorInput) attachFileToInput(this.refs.vectorInput, null);
      return true;
    }

    // The pattern/customer bitmap is asynchronous. Submission must export the
    // latest composite rather than whichever layer happened to finish first.
    await this.#syncDisplayArtwork();
    if (token !== this.#snapshotToken) return true;

    const exportTarget = this.#resolvePlacementExportTarget();
    let result = null;
    try {
      result = await this.#remoteRenderer.export?.({
        placementTarget: exportTarget?.target ?? null,
        alignment: exportTarget?.alignment ?? null,
        includeVector: Boolean(this.refs.vectorInput),
      });
    } catch {
      result = null;
    }

    if (token !== this.#snapshotToken) return true;
    if (this.#config?.placements.enabled) {
      const placementSvg = this.#overlay?.querySelector('svg')?.cloneNode(true);
      const local = placementSvg && exportTarget ? await exportProductPlacementPng(placementSvg, exportTarget.target, exportTarget.alignment) : null;
      result = { ...result, png: local?.file ?? null, partial: Boolean(result?.partial || local?.partial) };
    }
    if (token !== this.#snapshotToken) return true;

    // Optional artifacts only — never treat missing export as a hard failure.
    if (this.refs.mockupInput) {
      const attached = Boolean(result?.png && !result.partial && attachFileToInput(this.refs.mockupInput, result.png));
      if (!attached) attachFileToInput(this.refs.mockupInput, null);
    }

    if (this.refs.vectorInput) {
      const attached = Boolean(
        result?.vector && !result.partial && attachFileToInput(this.refs.vectorInput, result.vector)
      );
      if (!attached) attachFileToInput(this.refs.vectorInput, null);
    }

    return true;
  }

  /** @returns {boolean} */
  #isPreviewEnabled() {
    const config = this.#config;
    const state = this.#store?.state;
    return Boolean(this.dataset.livePreviewDisabled !== 'true' && !(this.dataset.patchConditionKey && this.hidden) && config && state && getMaterialGroup(config, state.materialGroup)?.previewEnabled);
  }

  #clearGeneratedFiles() {
    if (this.#snapshotTimer !== null) clearTimeout(this.#snapshotTimer);
    this.#snapshotTimer = null;
    this.#snapshotToken += 1;
    if (this.refs.mockupInput) attachFileToInput(this.refs.mockupInput, null);
    if (this.refs.vectorInput) attachFileToInput(this.refs.vectorInput, null);
  }

  /**
   * @returns {{ target: Element, alignment: import('./patch-config.js').PatchAlignment } | null}
   */
  #resolvePlacementExportTarget() {
    const config = this.#config;
    const root = this.#findGalleryRoot();
    if (!config || !root) return null;

    const active = this.#remoteRenderer?.mountTarget;
    const physical = active && this.#basePlacement(active).sizingMode === 'physical';
    const candidates = physical ? [active] : [active, ...root.querySelectorAll('.product-media[data-media-id]')];
    const seen = new Set();

    for (const target of candidates) {
      if (!target || seen.has(target)) continue;
      seen.add(target);
      if (!target.querySelector('img')) continue;

      const baseAlignment = this.#basePlacement(target);
      if (baseAlignment.hide) continue;

      if (config.placements.enabled) {
        const { hostAspect, patchAspect } = this.#placementDimensions(target);
        const alignment = adjustedAlignment(baseAlignment, this.#store.state.preview.adjustment);
        if (!fitsBounds(alignment, hostAspect, patchAspect)) continue;
      }
      return {
        target,
        alignment: adjustedAlignment(
          baseAlignment,
          this.#store?.state.preview.adjustment ?? { x: 0, y: 0, size: 0 }
        ),
      };
    }

    return null;
  }

  // -- dragging -------------------------------------------------------------

  /**
   * Lets the customer drag and pinch the artwork directly on the product photo.
   *
   * Pointer events cover mouse, touch and pen with one code path. The range
   * inputs in the markup provide the keyboard-accessible equivalent, so nothing
   * here is the only route to a setting.
   *
   * @param {HTMLElement} overlay
   * @param {AbortSignal} signal
   */
  #bindDragging(overlay, signal) {
    // Browsers dispatch a separate click after pointerup, even when the pointer
    // moved. Without consuming it, releasing a patch drag can bubble into the
    // media gallery's click-to-zoom action.
    overlay.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { signal }
    );

    overlay.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.#store || this.#alignmentEditor) return;
        const { source } = this.#store.state.artwork;
        if (source !== 'upload' && source !== 'design' && !this.#store.state.isDieCut) return;

        overlay.setPointerCapture(event.pointerId);
        this.#pointers.set(event.pointerId, event);

        if (this.#pointers.size === 1) {
          this.#dragOrigin = {
            x: event.clientX,
            y: event.clientY,
            distance: 0,
            scale: this.#store.state.artwork.position.scale,
            patchSize: this.#store.state.preview.adjustment.size,
          };
        }

        overlay.classList.add('is-dragging');
      },
      { signal }
    );

    overlay.addEventListener(
      'pointermove',
      (event) => {
        if (!this.#pointers.has(event.pointerId)) return;

        // Stops the gallery treating the drag as a swipe to the next photo.
        event.preventDefault();
        event.stopPropagation();
        this.#pointers.set(event.pointerId, event);

        if (this.#pointers.size >= 2) {
          this.#handlePinch();
          return;
        }

        this.#handleDrag(event, overlay);
      },
      { signal }
    );

    const endPointer = (/** @type {PointerEvent} */ event) => {
      this.#pointers.delete(event.pointerId);

      if (this.#pointers.size === 0) {
        this.#dragOrigin = null;
        overlay.classList.remove('is-dragging');
      }
    };

    overlay.addEventListener('pointerup', endPointer, { signal });
    overlay.addEventListener('pointercancel', endPointer, { signal });
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} overlay
   */
  #handleDrag(event, overlay) {
    const store = this.#store;
    const origin = this.#dragOrigin;
    const shape = store ? getShape(store.state.shape, this.#customShapes) : null;
    if (!store || !origin || !shape) return;

    const rect = overlay.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    let movedX = event.clientX - origin.x;
    let movedY = event.clientY - origin.y;

    if (store.state.isDieCut || this.#config?.placements.enabled) {
      const host = overlay.parentElement?.getBoundingClientRect();
      if (!host || host.width === 0 || host.height === 0) return;

      const adjustment = movePreviewAdjustment(
        store.state.preview.adjustment,
        (movedX / host.width) * 100,
        (movedY / host.height) * 100
      );

      this.#dragOrigin = { ...origin, x: event.clientX, y: event.clientY };
      this.#setPreviewAdjustment(adjustment);
      return;
    }

    // The overlay is usually tilted to match the photo. Rotating the movement
    // back means dragging right walks the artwork along the patch, not the
    // screen, which is what the customer sees happening.
    if (this.#overlayRotation !== 0) {
      const radians = (-this.#overlayRotation * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      [movedX, movedY] = [movedX * cos - movedY * sin, movedX * sin + movedY * cos];
    }

    // `getBoundingClientRect` grows once the overlay is tilted, so measure with
    // `offsetWidth`, which stays the untilted width the alignment asked for.
    const width = overlay.offsetWidth || rect.width;

    // Translate pixels into viewBox units, then into a percentage of the safe
    // area, which is the unit the artwork position is stored in.
    const unitsPerPixel = shape.width / width;
    const deltaX = (movedX * unitsPerPixel * 100) / shape.safeArea.width;
    const deltaY = (movedY * unitsPerPixel * 100) / shape.safeArea.height;

    const current = store.state.artwork.position;

    this.#dragOrigin = { ...origin, x: event.clientX, y: event.clientY };

    store.update({
      artwork: { position: clampPosition({ x: current.x + deltaX, y: current.y + deltaY }, current) },
    });

    this.#syncPositionInputs(store.state.artwork.position);
  }

  #handlePinch() {
    const store = this.#store;
    const origin = this.#dragOrigin;
    if (!store || !origin) return;

    const [first, second] = [...this.#pointers.values()];
    if (!first || !second) return;

    const distance = Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);

    if (origin.distance === 0) {
      this.#dragOrigin = {
        ...origin,
        distance,
        scale: store.state.artwork.position.scale,
        patchSize: store.state.preview.adjustment.size,
      };
      return;
    }

    if (store.state.isDieCut || this.#config?.placements.enabled) {
      const adjustment = resizePreviewAdjustment(
        { ...store.state.preview.adjustment, size: origin.patchSize },
        distance / origin.distance
      );

      this.#setPreviewAdjustment(adjustment);
      return;
    }

    const current = store.state.artwork.position;
    const scale = origin.scale * (distance / origin.distance);

    store.update({ artwork: { position: clampPosition({ scale }, current) } });
    this.#syncPositionInputs(store.state.artwork.position);
  }

  /**
   * Keeps the numeric controls in step with direct manipulation.
   *
   * @param {import('./patch-state.js').PatchPosition} position
   */
  #syncPositionInputs(position) {
    /** @type {Record<string, number>} */
    const values = {
      'position-x': position.x,
      'position-y': position.y,
      'position-scale': position.scale,
      'position-rotation': position.rotation,
    };

    for (const [control, value] of Object.entries(values)) {
      const input = this.querySelector(`[data-patch-control="${control}"]`);
      if (input instanceof HTMLInputElement) input.value = String(Math.round(value * 100) / 100);
    }
  }

  /**
   * @param {import('./patch-state.js').PatchPreviewAdjustment} adjustment
   */
  #syncPreviewAdjustmentInputs(adjustment) {
    /** @type {Record<string, number>} */
    const values = {
      'preview-x': adjustment.x,
      'preview-y': adjustment.y,
      'preview-size': adjustment.size,
    };

    for (const [control, value] of Object.entries(values)) {
      const input = this.querySelector(`[data-patch-control="${control}"]`);
      if (input instanceof HTMLInputElement) input.value = String(value);
    }
  }

  // -- alignment editor -----------------------------------------------------

  async #loadAlignmentEditor() {
    if (!this.#overlay || !this.#config || !this.#store || this.#alignmentEditor) return;

    try {
      const { mountAlignmentEditor } = await import('@patch/alignment-editor');

      this.#alignmentEditor = mountAlignmentEditor({
        overlay: this.#overlay,
        config: this.#config,
        customShapes: this.#customShapes,
        getMedia: () => (this.#remoteRenderer?.mountTarget ? describeMedia(this.#remoteRenderer.mountTarget, this.#findGalleryRoot()) : null),
        listMedia: () => listMedia(this.#findGalleryRoot()),
        onChange: () => {
          const placements = this.#config.placements;
          if (!placements.items[this.#store.state.preview.placementId]?.available) {
            const next = placements.items[placements.configuredDefault]?.available ? placements.configuredDefault : PLACEMENT_IDS.find(id => placements.items[id].available) ?? null;
            placements.default = next;
            this.#store.update({ preview: { placementId: next, adjustment: { x: 0, y: 0, size: 0 } } });
          }
          this.#populatePlacements(); this.#clearGeneratedFiles(); this.#syncProperties(this.#store.state);
        },
      });
    } catch (error) {
      console.warn('[patch] alignment editor unavailable', error);
    }
  }

  // -- messaging ------------------------------------------------------------

  /**
   * @param {string} message
   */
  #showError(message) {
    const region = this.refs.errors;
    if (!region) return;

    if (this.refs.errorMessage) this.refs.errorMessage.textContent = message;

    region.hidden = false;

    // Move focus to the start of the message so screen reader users hear it
    // from the beginning rather than mid-sentence.
    requestAnimationFrame(() => {
      this.refs.errorHeading?.focus();
    });
  }

  #clearError() {
    const region = this.refs.errors;
    if (!region) return;

    region.hidden = true;
    if (this.refs.errorMessage) this.refs.errorMessage.textContent = '';
  }
}

/**
 * @param {HTMLInputElement | undefined} input
 * @param {string} value
 */
function setInputValue(input, value) {
  if (!input) return;

  // Empty properties are disabled so Shopify does not show blank rows in the cart.
  input.disabled = value === '';
  if (input.value !== value) input.value = value;
}

if (!customElements.get('patch-designer')) {
  customElements.define('patch-designer', PatchDesigner);
}

export { PatchDesigner };
